import React, { useEffect, useRef } from 'react';

/**
 * Shared shell for every modal (admin Generate Link screens and the onboarding form).
 *
 * The admin header is z-50 and the sidebar is z-[60], so a modal has to sit above
 * both (z-[100]) — otherwise the header stays visible on top of the backdrop.
 *
 * Scroll locking: the admin shell keeps <body> at overflow-hidden and scrolls an
 * inner <main>, so locking the body alone does nothing. Every scrollable ancestor
 * is locked instead, which stops the page behind the modal from moving.
 */
const ModalShell = ({ title, subtitle, onClose, children, maxWidth = 'max-w-4xl' }) => {
    const overlayRef = useRef(null);
    const onCloseRef = useRef(onClose);

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onCloseRef.current?.();
        };
        document.addEventListener('keydown', handleKeyDown);

        // Lock the page behind the modal
        const restore = [];
        const lock = (element) => {
            restore.push([element, element.style.overflow]);
            element.style.overflow = 'hidden';
        };

        lock(document.body);
        let node = overlayRef.current?.parentElement;
        while (node && node !== document.body) {
            const { overflowY } = window.getComputedStyle(node);
            if (overflowY === 'auto' || overflowY === 'scroll') lock(node);
            node = node.parentElement;
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            restore.forEach(([element, value]) => { element.style.overflow = value; });
        };
    }, []);

    return (
        <div
            ref={overlayRef}
            className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto overscroll-contain bg-[#211b4f]/55 p-3 backdrop-blur-sm sm:items-center sm:p-6"
        >
            <div
                className={`rize-modal-shell relative my-4 flex max-h-[92vh] w-full ${maxWidth} flex-col overflow-hidden rounded-2xl border border-white/60 bg-white shadow-2xl`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-gradient-to-r from-white to-[#fff8f3] px-5 py-4 sm:px-8">
                    <div className="min-w-0">
                        <h3 className="break-words text-lg font-bold text-[#0a1445] sm:text-xl">{title}</h3>
                        {subtitle && <p className="mt-0.5 text-sm text-slate-500 break-words">{subtitle}</p>}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 p-2 text-slate-500 transition-colors hover:bg-orange-100 hover:text-[#fb6635] focus:outline-none focus:ring-2 focus:ring-orange-300"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {children}
            </div>
        </div>
    );
};

export default ModalShell;
